import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import LockResetRoundedIcon from '@mui/icons-material/LockResetRounded';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import {
    ActionCodeOperation,
    applyActionCode,
    checkActionCode,
    confirmPasswordReset,
    verifyPasswordResetCode,
} from 'firebase/auth';
import { Link, useSearchParams } from 'react-router-dom';
import { auth } from '../firebase';
import { PublicLayout } from './PublicLayout';

type SupportedActionMode = 'verifyEmail' | 'resetPassword' | 'recoverEmail';
type PageStatus = 'checking' | 'ready' | 'submitting' | 'success' | 'error';

const supportedModes = new Set<SupportedActionMode>(['verifyEmail', 'resetPassword', 'recoverEmail']);
const publicPlanCodes = new Set(['starter', 'premium']);
const signupDraftIdPattern = /^[A-Za-z0-9_-]{20,80}$/;

// Only preserve the public signup state generated by SuperPenguin; all other redirect targets fail closed.
export function getAllowedEmailActionContinueUrl(value: string | null, origin: string): string | null {
    if (!value) return null;
    try {
        const dashboardOrigin = new URL(origin);
        const candidate = new URL(value);
        const pathname = candidate.pathname.length > 1 ? candidate.pathname.replace(/\/+$/, '') : candidate.pathname;
        if (candidate.origin !== dashboardOrigin.origin || pathname !== '/signup') return null;

        const safeUrl = new URL('/signup', dashboardOrigin.origin);
        const plan = candidate.searchParams.get('plan');
        const version = candidate.searchParams.get('version');
        const interval = candidate.searchParams.get('interval');
        const draft = candidate.searchParams.get('draft');
        const units = candidate.searchParams.get('units');
        const parsedUnits = Number(units);
        if (
            plan && publicPlanCodes.has(plan)
            && version && /^[A-Za-z0-9._-]{1,120}$/.test(version)
            && (interval === 'monthly' || interval === 'annual')
        ) {
            const safeParams = new URLSearchParams({ plan, version, interval });
            if (draft && signupDraftIdPattern.test(draft)) safeParams.set('draft', draft);
            if (plan === 'premium' && Number.isInteger(parsedUnits) && parsedUnits >= 1 && parsedUnits <= 20) {
                safeParams.set('units', String(parsedUnits));
            }
            safeUrl.search = safeParams.toString();
        }
        return safeUrl.toString();
    } catch {
        return null;
    }
}

// Firebase cannot distinguish every already-used action code from an expired one, so recovery copy covers both safely.
function actionCodeErrorMessage(error: unknown, mode: SupportedActionMode | null): string {
    const code = (error as { code?: string })?.code;
    if (mode === 'recoverEmail' && (code === 'auth/expired-action-code' || code === 'auth/invalid-action-code')) {
        return 'This recovery link has already been used, has expired, or was replaced by a newer link. If you still need help, reset your password to secure the account.';
    }
    if (code === 'auth/expired-action-code') {
        return 'This link has expired. Request a new email and use only the most recent link.';
    }
    if (code === 'auth/invalid-action-code') {
        return 'This link has already been used, is invalid, or was replaced by a newer link. Request a new email and try again.';
    }
    if (code === 'auth/user-disabled' || code === 'auth/user-not-found') {
        return 'This account is no longer available. Contact SuperPenguin support if you need help.';
    }
    if (code === 'auth/weak-password') {
        return 'Choose a stronger password with at least 8 characters.';
    }
    return 'We could not complete this email action. Please request a new email and try again.';
}

// The custom handler validates links on load but deliberately waits for a button press before consuming a code.
export const AuthActionPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const rawMode = searchParams.get('mode');
    const mode = rawMode && supportedModes.has(rawMode as SupportedActionMode)
        ? rawMode as SupportedActionMode
        : null;
    const actionCode = searchParams.get('oobCode') || '';
    const continueUrl = useMemo(
        () => getAllowedEmailActionContinueUrl(searchParams.get('continueUrl'), window.location.origin),
        [searchParams]
    );
    const [status, setStatus] = useState<PageStatus>('checking');
    const [accountEmail, setAccountEmail] = useState('');
    const [previousEmail, setPreviousEmail] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    useEffect(() => {
        let cancelled = false;
        if (!mode || !actionCode) {
            setError('This email action link is incomplete or unsupported. Request a new email and try again.');
            setStatus('error');
            return () => { cancelled = true; };
        }

        setStatus('checking');
        setError(null);

        // These calls inspect validity only. applyActionCode and confirmPasswordReset remain click-gated below.
        const validateAction = async () => {
            try {
                if (mode === 'resetPassword') {
                    const email = await verifyPasswordResetCode(auth, actionCode);
                    if (!cancelled) setAccountEmail(email);
                } else {
                    const info = await checkActionCode(auth, actionCode);
                    const expectedOperation = mode === 'verifyEmail'
                        ? ActionCodeOperation.VERIFY_EMAIL
                        : ActionCodeOperation.RECOVER_EMAIL;
                    if (info.operation !== expectedOperation) {
                        const operationError = new Error('Action code operation does not match the requested mode') as Error & { code: string };
                        operationError.code = 'auth/invalid-action-code';
                        throw operationError;
                    }
                    if (!cancelled) {
                        setAccountEmail(info.data.email || '');
                        setPreviousEmail(info.data.previousEmail || '');
                    }
                }
                if (!cancelled) setStatus('ready');
            } catch (validationError: unknown) {
                if (!cancelled) {
                    setError(actionCodeErrorMessage(validationError, mode));
                    setStatus('error');
                }
            }
        };
        void validateAction();
        return () => { cancelled = true; };
    }, [actionCode, mode]);

    const applyEmailAction = async () => {
        if (!mode || mode === 'resetPassword' || status !== 'ready') return;
        setStatus('submitting');
        setError(null);
        try {
            await applyActionCode(auth, actionCode);

            // Refresh an already-open signup session when possible; the completed action remains successful if refresh fails.
            if (mode === 'verifyEmail' && auth.currentUser) {
                try {
                    await auth.currentUser.reload();
                    await auth.currentUser.getIdToken(true);
                } catch {
                    // The original signup tab can refresh the user independently.
                }
            }
            setStatus('success');
        } catch (actionError: unknown) {
            setError(actionCodeErrorMessage(actionError, mode));
            setStatus('error');
        }
    };

    const resetPassword = async (event: FormEvent) => {
        event.preventDefault();
        if (mode !== 'resetPassword' || status !== 'ready') return;
        setError(null);
        if (newPassword.length < 8) {
            setError('Choose a password with at least 8 characters.');
            return;
        }
        if (newPassword !== confirmPassword) {
            setError('The passwords do not match.');
            return;
        }

        setStatus('submitting');
        try {
            await confirmPasswordReset(auth, actionCode, newPassword);
            setNewPassword('');
            setConfirmPassword('');
            setStatus('success');
        } catch (resetError: unknown) {
            setError(actionCodeErrorMessage(resetError, mode));
            setStatus('ready');
        }
    };

    const destination = continueUrl || new URL('/', window.location.origin).toString();
    const signedInEmail = (auth.currentUser?.email || '').trim().toLowerCase();
    const isMatchingVerifiedSession = mode === 'verifyEmail' && !!accountEmail &&
        signedInEmail === accountEmail.trim().toLowerCase();
    const resumeDestination = continueUrl ? new URL(continueUrl) : null;
    if (resumeDestination) resumeDestination.searchParams.set('resume', '1');
    const successDestination = mode === 'verifyEmail' && !isMatchingVerifiedSession
        ? resumeDestination?.toString() || destination
        : destination;
    const destinationLabel = mode === 'verifyEmail' && !isMatchingVerifiedSession
        ? 'Sign in to resume clinic setup'
        : continueUrl ? 'Continue setting up your clinic' : 'Continue to sign in';

    if (status === 'checking') {
        return (
            <PublicLayout compact>
                <main className='saas-auth-action-shell' aria-live='polite' aria-busy='true'>
                    <span className='saas-spinner' aria-hidden='true' />
                    <h1>Checking your link…</h1>
                    <p>No account change will be made until you confirm it.</p>
                </main>
            </PublicLayout>
        );
    }

    if (status === 'error') {
        return (
            <PublicLayout compact>
                <main className='saas-auth-action-shell' role='alert'>
                    <span className='saas-status-icon saas-status-icon--error' aria-hidden='true'>
                        <ErrorOutlineRoundedIcon fontSize='inherit' />
                    </span>
                    <h1>Email link unavailable</h1>
                    <p>{error}</p>
                    <Link className='saas-button' to='/'>Return to sign in</Link>
                    <a className='saas-text-link' href='mailto:support@superpenguin.com'>Contact support</a>
                </main>
            </PublicLayout>
        );
    }

    if (status === 'success') {
        const title = mode === 'verifyEmail'
            ? 'Email verified'
            : mode === 'recoverEmail'
                ? 'Email address restored'
                : 'Password changed';
        const message = mode === 'verifyEmail'
            ? isMatchingVerifiedSession
                ? 'Your email address has been verified. You can continue setting up your clinic.'
                : 'Your email address has been verified. Return to your original signup tab and press “I have verified my email”. If that tab is no longer open, sign in below to resume setting up your clinic.'
            : mode === 'recoverEmail'
                ? `Your account email has been restored${accountEmail ? ` to ${accountEmail}` : ''}. If you did not request the original change, reset your password too.`
                : 'Your new password is ready. You can now sign in to SuperPenguin.';
        return (
            <PublicLayout compact>
                <main className='saas-auth-action-shell' aria-live='polite'>
                    <span className='saas-status-icon saas-status-icon--success' aria-hidden='true'>
                        <CheckCircleOutlineRoundedIcon fontSize='inherit' />
                    </span>
                    <h1>{title}</h1>
                    <p>{message}</p>
                    <a className='saas-button' href={successDestination}>{destinationLabel}</a>
                </main>
            </PublicLayout>
        );
    }

    if (mode === 'resetPassword') {
        return (
            <PublicLayout compact>
                <main className='saas-auth-action-shell'>
                    <section className='saas-form-card' aria-labelledby='auth-action-heading'>
                        <span className='saas-status-icon' aria-hidden='true'><LockResetRoundedIcon fontSize='inherit' /></span>
                        <h1 id='auth-action-heading'>Choose a new password</h1>
                        <p>Reset the password for <strong>{accountEmail}</strong>.</p>
                        <form onSubmit={resetPassword}>
                            <label>
                                New password
                                <input
                                    required
                                    minLength={8}
                                    type='password'
                                    autoComplete='new-password'
                                    value={newPassword}
                                    onChange={(event) => setNewPassword(event.target.value)}
                                />
                            </label>
                            <label>
                                Confirm new password
                                <input
                                    required
                                    minLength={8}
                                    type='password'
                                    autoComplete='new-password'
                                    value={confirmPassword}
                                    onChange={(event) => setConfirmPassword(event.target.value)}
                                />
                            </label>
                            {error && <p className='saas-inline-alert saas-inline-alert--error' role='alert'>{error}</p>}
                            <button className='saas-button saas-button--wide' type='submit' disabled={status === 'submitting'}>
                                {status === 'submitting' ? 'Changing password…' : 'Change password'}
                            </button>
                        </form>
                    </section>
                </main>
            </PublicLayout>
        );
    }

    const isRecovery = mode === 'recoverEmail';
    return (
        <PublicLayout compact>
            <main className='saas-auth-action-shell'>
                <section className='saas-form-card' aria-labelledby='auth-action-heading'>
                    <span className='saas-status-icon' aria-hidden='true'>
                        {isRecovery ? <SecurityRoundedIcon fontSize='inherit' /> : <MailOutlineRoundedIcon fontSize='inherit' />}
                    </span>
                    <h1 id='auth-action-heading'>{isRecovery ? 'Restore your email address' : 'Verify your email'}</h1>
                    <p>
                        {isRecovery
                            ? <>Restore this account to <strong>{accountEmail || 'its previous email address'}</strong>{previousEmail ? ` instead of ${previousEmail}` : ''}.</>
                            : <>Confirm that <strong>{accountEmail || 'this email address'}</strong> belongs to you.</>}
                    </p>
                    <p className='saas-inline-alert'>This link will only be used when you press the button below.</p>
                    {error && <p className='saas-inline-alert saas-inline-alert--error' role='alert'>{error}</p>}
                    <button
                        className='saas-button saas-button--wide'
                        type='button'
                        disabled={status === 'submitting'}
                        onClick={() => { void applyEmailAction(); }}
                    >
                        {status === 'submitting'
                            ? (isRecovery ? 'Restoring email…' : 'Verifying email…')
                            : (isRecovery ? 'Restore email address' : 'Verify email')}
                    </button>
                    <Link className='saas-link-button' to='/'>Cancel and return to sign in</Link>
                </section>
            </main>
        </PublicLayout>
    );
};
